/*
 * Zarafae look row: a framed row of look cards that becomes a snap row with dots on phones and
 * shows square arrows only while it overflows (critique items collection-and-recs-grids and
 * zf-home-products). Used by sections/product-recommendations.liquid.
 *
 * Markup contract:
 *   <zf-look-row>
 *     <div data-zf-row-viewport>            the scroll container
 *       <ul> <li data-zf-row-item>…</li> </ul>
 *     </div>
 *     <button data-zf-row-prev hidden>  <button data-zf-row-dot="0">…  <button data-zf-row-next hidden>
 *   </zf-look-row>
 *
 * Editor-safe: one window flag, one document-level click listener, and per-element observers that
 * are created in connectedCallback and removed in disconnectedCallback. Section re-renders in the
 * theme editor, the recommendations fetch (Section Rendering API morph) and shopify:section:load
 * all re-initialise. No libraries.
 */
(function () {
  if (window.__zfLookRow) return;
  window.__zfLookRow = true;

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class ZfLookRow extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.teardown();
    }

    init() {
      this.teardown();

      this.viewport = this.querySelector('[data-zf-row-viewport]');
      if (!this.viewport) return;

      this.items = Array.from(this.querySelectorAll('[data-zf-row-item]'));
      this.dots = Array.from(this.querySelectorAll('[data-zf-row-dot]'));
      this.prev = this.querySelector('[data-zf-row-prev]');
      this.next = this.querySelector('[data-zf-row-next]');
      this.ratios = new Map();

      this.handleScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.syncArrows();
        });
      };
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.sync());
        this.resizeObserver.observe(this.viewport);
        if (this.viewport.firstElementChild) this.resizeObserver.observe(this.viewport.firstElementChild);
      } else {
        this.handleResize = () => this.sync();
        window.addEventListener('resize', this.handleResize);
      }

      if (this.dots.length && typeof IntersectionObserver === 'function') {
        this.intersectionObserver = new IntersectionObserver((entries) => this.handleIntersect(entries), {
          root: this.viewport,
          threshold: [0, 0.6, 1],
        });
        this.items.forEach((item) => this.intersectionObserver.observe(item));
      }

      // Cards swapped in place (theme editor, morphs) get fresh observers on the next frame.
      if (typeof MutationObserver === 'function') {
        this.mutationObserver = new MutationObserver(() => {
          if (this.refreshFrame) return;
          this.refreshFrame = requestAnimationFrame(() => {
            this.refreshFrame = 0;
            if (this.isConnected) this.init();
          });
        });
        this.mutationObserver.observe(this, { childList: true, subtree: true });
      }

      this.sync();
    }

    teardown() {
      if (this.viewport && this.handleScroll) this.viewport.removeEventListener('scroll', this.handleScroll);
      if (this.handleResize) window.removeEventListener('resize', this.handleResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.handleScroll = null;
      this.handleResize = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.mutationObserver = null;
      this.scrollFrame = 0;
      this.refreshFrame = 0;
    }

    sync() {
      if (!this.viewport) return;
      const overflowing = this.viewport.scrollWidth > this.viewport.clientWidth + 1;
      this.toggleAttribute('data-overflowing', overflowing);
      this.syncArrows();
    }

    syncArrows() {
      if (!this.viewport) return;
      const overflowing = this.hasAttribute('data-overflowing');
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      const position = Math.abs(this.viewport.scrollLeft);

      // aria-disabled rather than disabled, so a focused arrow keeps focus at the end of the row.
      if (this.prev) {
        this.prev.hidden = !overflowing;
        this.prev.setAttribute('aria-disabled', String(position <= 1));
      }
      if (this.next) {
        this.next.hidden = !overflowing;
        this.next.setAttribute('aria-disabled', String(position >= max - 1));
      }
    }

    handleIntersect(entries) {
      entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));
      let current = -1;
      let best = 0;
      this.items.forEach((item, index) => {
        const ratio = this.ratios.get(item) || 0;
        if (ratio >= 0.6 && ratio > best) {
          best = ratio;
          current = index;
        }
      });
      if (current > -1) this.setCurrent(current);
    }

    setCurrent(index) {
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    step(direction) {
      if (!this.viewport) return;
      const item = this.items[0];
      const distance = item ? item.getBoundingClientRect().width : this.viewport.clientWidth;
      const rtl = getComputedStyle(this.viewport).direction === 'rtl';
      this.viewport.scrollBy({
        left: direction * (rtl ? -1 : 1) * distance,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    goTo(index) {
      const item = this.items[index];
      if (!item || !this.viewport) return;
      const style = getComputedStyle(this.viewport);
      const scrollPadding = parseFloat(style.scrollPaddingInlineStart || style.scrollPaddingLeft) || 0;
      const offset = item.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left;
      this.viewport.scrollTo({
        left: this.viewport.scrollLeft + offset - scrollPadding,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      this.setCurrent(index);
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const control = target.closest('[data-zf-row-prev], [data-zf-row-next], [data-zf-row-dot]');
    if (!control) return;
    const row = control.closest('zf-look-row');
    if (!(row instanceof ZfLookRow)) return;
    if (control.getAttribute('aria-disabled') === 'true') return;

    if (control.hasAttribute('data-zf-row-dot')) {
      row.goTo(Number(control.getAttribute('data-zf-row-dot')) || 0);
    } else {
      row.step(control.hasAttribute('data-zf-row-next') ? 1 : -1);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!(root instanceof Element)) return;
    root.querySelectorAll('zf-look-row').forEach((row) => {
      if (row instanceof ZfLookRow) row.init();
    });
  });

  if (!customElements.get('zf-look-row')) {
    customElements.define('zf-look-row', ZfLookRow);
  }
})();
